import { SpatialQueryGrid } from './SpatialQueryGrid';

/**
 * Capsule-footprint floor sampling. Casts five downward rays — center +
 * four cardinal offsets at `radius` — and returns the **highest** sample
 * at or below `currentY + maxStep`. The maxStep budget gates upward
 * stepping; downward distance is unbounded so the helper correctly
 * catches a falling player on any surface their body radius would touch.
 *
 * **The cases this is built for** (all BVH-precision artifacts that
 * volgrid's 0.1 m voxel quantization had masked):
 *
 * 1. *Seam fall-through.* Center ray slips through an infinitesimal gap
 *    between two flush triangle islands (adjacent floor tiles, building
 *    floor meeting terrain, stair-tread seams). Any cardinal that lands
 *    on the upper surface wins via "highest" — center's wrong answer is
 *    dominated by the correct neighbours.
 *
 * 2. *Mid-fall onto a thin board next to the trajectory.* A 9 cm board
 *    that the player's body radius reaches (one cardinal on the board,
 *    center over the floor below). Without "highest wins" the helper
 *    picks the floor and the player tunnels past the board to the
 *    terrain below — the bug J-debug captured.
 *
 * **The trade-off.** With `radius > 0`, walking up to a thin board's
 * east edge snaps the player up onto the board the moment one foot
 * touches it (the same mechanism that catches mid-fall landings). The
 * snap-down on stepping off is symmetric — ~5 cm for a 9 cm board.
 * That visible "step up / step down" is a feature, not a bug: it matches
 * the rendered character's visual extent (which is wider than the agent
 * radius for most humanoid models) instead of a literal point-XZ pin.
 *
 * **Semantics.** Returns `null` when every sample missed. With `radius = 0`
 * this degenerates to a single-ray query — bit-identical to the pre-helper
 * behaviour for point bodies (projectiles).
 */
export function sampleFootprintFloorHeight(
  grid: SpatialQueryGrid,
  x: number,
  currentY: number,
  z: number,
  radius: number,
  maxStep: number,
): number | null {
  // Ray origin slightly above the player. Two reasons it must not collapse
  // to `currentY`:
  //   1. The BVH ray-triangle test rejects hits at `t < 1e-7` (Möller-
  //      Trumbore precision guard) — a player standing exactly on a thin
  //      board top has `position.y === boardTop`, so an origin at
  //      `currentY` produces `t === 0` and the board top is silently
  //      culled.
  //   2. When `maxStep` is `Infinity` (terrain-only scenes that don't
  //      impose an indoor step limit), naive `currentY + Infinity` is a
  //      degenerate ray origin. We cap with a small constant so the
  //      origin stays finite.
  const ORIGIN_FLOOR_M = 1.0;
  const stepBudget = Number.isFinite(maxStep) ? maxStep : ORIGIN_FLOOR_M;
  const originY = currentY + Math.max(stepBudget, 0.05);

  if (radius <= 0) {
    const s = grid.getFloorHeightBelow(x, originY, z);
    if (s === null || s === undefined) return null;
    if (s - currentY > maxStep) return null;
    return s;
  }

  // Five samples: center + cardinals. Diagonals add cost without changing
  // the result meaningfully — cardinals already span the capsule diameter.
  const offsets: [number, number][] = [
    [0, 0],
    [radius, 0],
    [-radius, 0],
    [0, radius],
    [0, -radius],
  ];
  let best: number | null = null;
  for (const [dx, dz] of offsets) {
    const s = grid.getFloorHeightBelow(x + dx, originY, z + dz);
    if (s === null || s === undefined) continue;
    // Reject unreachable ledges (more than maxStep above the player) —
    // they can't be stepped up to. Floors below are always candidates.
    if (s - currentY > maxStep) continue;
    if (best === null || s > best) best = s;
  }
  return best;
}
